import { handleOf } from "../common/objectHandle.js";
import { BufferGeometry, Color, Texture } from "three";
import { ContextKeyExpr } from "../../contextkey/common/contextkey";
import { IProperty, PropertyMetadata } from "../../properties/common/properties";
import { ColorLike, getMatrixDimension, isColorLike, isMatrixLike, isTextureLike, isTexturePropertyLike, isVectorPropertyLike } from "../../properties/common/types";
import { geometryAttributeGpuTypeToString, geometryAttributeUsageToString, isAnimationActionLike, isAnimationClipLike, isAnimationMixer, isCamera, isCompressedTexture, isCubeTexture, isDataTexture, isGeometry, isInstancedMeshLike, isInterleavedBufferAttribute, isLayermask, isLight, isLikelyColorInputSlot, isLikelyMaterialTextureInputSlot, isMaterial, isMeshLike, isObject3D, isRenderer, isScene, isSkeleton, isSkinnedMeshLike, isTexture, isTSLNode } from "./helper.js";
import { isMathNode, isNodeLike, isNodeMaterial, isOperatorNode, isPassNode, NodeLike } from "./helper.nodes";
import { estimateTextureMemory, getTextureDataTypeAsString, getTexturePixelFormatAsString } from "./helper.textures.js";
import { Needle } from "./propertyReader.js";
import { resolveComponentName } from "./componentName";
import { AttributeGPUType, PixelFormatGPU, TextureDataType } from "three";
import * as THREE from "three";
import { toHumanReadableBytes, toHumanReadableCount, toMaxFixed } from "../../../base/common/humanize";
import { PropertyPicker } from "../../search/browser/propertyPicker";
import { NeedleMeta } from "../../../base/common/$__needle.types";



const object3DHidden = [
    // "position", "rotation", "scale"
    "modelViewMatrix", "matrixWorld", "normalMatrix"
]

/**
 * Enum options for a texture property, derived from three's own numeric constants whose export
 * name ends in `suffix` (e.g. "Format", "Mapping"). Deriving instead of hand-listing keeps the
 * list complete — including compressed pixel formats — so the inspector always resolves a label
 * for the current value, and it can never drift from three's constants.
 */
function threeConstantEnumOptions(suffix: string, exclude?: (name: string) => boolean) {
    return Object.entries(THREE)
        .filter(([name, value]) =>
            typeof value === "number" && name.endsWith(suffix) && !(exclude?.(name) ?? false))
        .map(([label, value]) => ({ label, value: value as number }))
        .sort((a, b) => a.value - b.value);
}

// All *Format constants (uncompressed + depth + compressed).
const texturePixelFormatOptions = threeConstantEnumOptions("Format");
// Texture.mapping constants — exclude the tone-mapping enums, which also end in "Mapping".
const textureMappingOptions = threeConstantEnumOptions("Mapping", name => name.endsWith("ToneMapping"));
// BufferAttribute.gpuType — the data-type constants (FloatType, ShortType, …).
const attributeTypeOptions = threeConstantEnumOptions("Type");
// BufferAttribute.usage — Static/Dynamic/Stream × Draw/Read/Copy.
const bufferUsageOptions = threeConstantEnumOptions("Usage");

/** A BufferAttribute or InterleavedBufferAttribute — anything carrying vertex data. */
function isBufferAttributeLike(owner: object): boolean {
    return "itemSize" in owner && ("array" in owner || "data" in owner);
}

function getGeometryIndexTopologyLabel(geometry: object): "point" | "line" | "triangle" {
    const type = "type" in geometry && typeof (geometry as { type?: unknown }).type === "string"
        ? ((geometry as { type: string }).type)
        : geometry.constructor?.name ?? "";

    if (type.includes("Wireframe") || type.includes("Edges") || type.includes("Line")) {
        return "line";
    }
    if (type.includes("Points") || type.includes("Point")) {
        return "point";
    }
    return "triangle";
}

function getGeometryIndexPlaceholder(owner: object, currentValue: unknown): string | undefined {
    if (!isGeometry(owner) || !currentValue || typeof currentValue !== "object") {
        return undefined;
    }

    const indexLike = currentValue as { count?: unknown };
    const count = typeof indexLike.count === "number" ? indexLike.count : Number.NaN;
    if (!Number.isFinite(count) || count <= 0) {
        return undefined;
    }

    const topology = getGeometryIndexTopologyLabel(owner);
    const divisor = topology === "triangle" ? 3 : topology === "line" ? 2 : 1;
    const primitiveCount = count / divisor;
    if (!Number.isFinite(primitiveCount) || primitiveCount <= 0) {
        return undefined;
    }

    const label = primitiveCount === 1 ? topology : `${topology}s`;
    return `${toHumanReadableCount(primitiveCount)} ${label}`;
}

function formatRangeSummary(start: number, count: number): string {
    if (!Number.isFinite(start) || !Number.isFinite(count) || count <= 0) {
        return `${start}–∞`;
    }
    return `${start}–${start + count - 1}`;
}

function getBoundingBoxPlaceholder(currentValue: unknown): string | undefined {
    if (!currentValue || typeof currentValue !== "object") {
        return undefined;
    }
    const boxLike = currentValue as {
        min?: { x?: unknown; y?: unknown; z?: unknown };
        max?: { x?: unknown; y?: unknown; z?: unknown };
    };
    const min = boxLike.min;
    const max = boxLike.max;
    const minX = typeof min?.x === "number" ? min.x : Number.NaN;
    const minY = typeof min?.y === "number" ? min.y : Number.NaN;
    const minZ = typeof min?.z === "number" ? min.z : Number.NaN;
    const maxX = typeof max?.x === "number" ? max.x : Number.NaN;
    const maxY = typeof max?.y === "number" ? max.y : Number.NaN;
    const maxZ = typeof max?.z === "number" ? max.z : Number.NaN;
    if ([minX, minY, minZ, maxX, maxY, maxZ].some(v => !Number.isFinite(v))) {
        return undefined;
    }
    return `${toMaxFixed(maxX - minX)}×${toMaxFixed(maxY - minY)}×${toMaxFixed(maxZ - minZ)}`;
}

function getBoundingSpherePlaceholder(currentValue: unknown): string | undefined {
    if (!currentValue || typeof currentValue !== "object") {
        return undefined;
    }
    const sphereLike = currentValue as { radius?: unknown };
    const radius = typeof sphereLike.radius === "number" ? sphereLike.radius : Number.NaN;
    if (!Number.isFinite(radius)) {
        return undefined;
    }
    return `${toMaxFixed(radius * 2)}d`;
}


// #region Postprocess
export function postprocessThreeProperty(property: IProperty): void {

    if (isRenderer(property.owner)) {
        switch (property.key) {
            case "isWebGLRenderer":
            case "capabilities":
            case "domElement":
            case "info":
            case "library":
            case "lighting":
            case "debug":
            case "xr":
                property.metadata.editable = false;
                property.editable = false;
                break;
        }
    }

    if (isObject3D(property.owner)) {
        if (object3DHidden.includes(property.key.toLowerCase())) {
            // if (this.debug) console.debug("Hide", key, value);
            property.hidden = true;
        }

        if (property.key.startsWith("world") && isVectorPropertyLike(property)) {
            property.editable = false;
        }

        const name = property.key.toLowerCase();
        switch (name) {
            case "id":
            case "uuid":
            case "type":
            case "guid":
            case "version":
                property.editable = false;
                break;
        }
        if (property.type === "boolean" && name.startsWith("is")) {
            property.editable = false; // e.g. isObject3D, isMesh, isLight, etc.
        }
    }

    if (isInstancedMeshLike(property.owner)) {
        switch (property.key) {
            case "count": // https://threejs.org/docs/?q=instanced#InstancedMesh.count
                property.editable = false;
                break;
        }
    }
    else if (isMeshLike(property.owner)) {
        switch (property.key) {
            case "count": // https://threejs.org/docs/?q=instanced#Mesh.count
                {
                    property.editable = false;
                }
        }
    }

    if (isMaterial(property.owner)) {
        switch (property.key) {
            case "version":
                property.editable = false;
                break;
        }
    }

    // mixer.time / action.time / action.repetitions are EDITABLE — writes
    // route through AnimationTimeAccessor (setTime / update(0) / setLoop),
    // which keeps the animation system consistent where raw assignment fails.

    if (isAnimationClipLike(property.owner)) {
        switch (property.key) {
            case "duration": // derived from the tracks — resetDuration() owns it
            case "uuid":
            case "tracks":
            case "blendMode":
                property.metadata.editable = false;
                property.editable = false;
                break;
        }
    }

    const isRendererProperty = isRenderer(property.owner);

    property.getTooltip = () => {
        const value = property.value;

        if (isNodeLike(value)) {
            return getNodeTooltipString(property.key, value, property.metadata);
        }
        else if (isTextureLike(value)) {
            return getTextureTooltipString(property.key, value, property.metadata);
        }
        else if (isGeometry(value)) {
            return getGeometryTooltipString(property.key, value, property.metadata);
        }
        else if (isMatrixLike(value)) {
            return getMatrixTooltipString(property.key, value, property.metadata);
        }
        else if (isColorLike(value)) {
            return getColorTooltipString(property.key, value, property.metadata);
        }
        else if (isRendererProperty) {
            switch (property.key) {
                case "library":
                case "lighting":
                case "info":
                case "capabilities":
                case "debug":
                case "xr":
                    let text = `**Slot**: ${property.metadata?.address || property.key}`;
                    text += `\n${formatAsMarkdownTable("Key", "Value", value as Record<string, any>)}\n`;
                    return text;
                    break;
            }
        }

        switch (property.key) {
            // js switch label
            case "defines":
            case "extensions":
            case "userData":

                let text = `**Slot**: ${property.metadata?.address || property.key}`;
                let key = "Key";
                if (property.key === "defines") key = "Define";
                else if (property.key === "extensions") key = "Name";

                if (typeof value === "object" && value !== null) {
                    const keys = Object.keys(value);
                    if (keys.length > 0) {
                        text += `\n${formatAsMarkdownTable(key, "Value", value as Record<string, any>)}`;
                    }
                    else {
                        text += `\n_(empty)_`;
                    }
                }
                return text;
                break;
        }


        return null;
    }
}

// #region tooltips


function formatAsMarkdownTable(header0: string, header1: string, values: Record<string, any>): string {
    let text = `\n\n| ${header0} | ${header1} |\n|---|---|\n`;
    for (let key of Object.keys(values)) {
        let value = values[key];
        if (Array.isArray(value)) {
            value = `length: ${value.length}, content: [${value.map(o => {
                if (typeof o === "object" && o !== null) return "object";
                return o;
            }).join(", ")}]`;
        }
        else if (typeof value === "object" && value !== null) {
            value = "object";
        }
        else if (typeof value === "function") {
            value = "function";
        }
        text += `| ${key} | ${value} |\n`;
    }
    return text;
}


function getNodeTooltipString(slot: string, value: NodeLike, meta?: PropertyMetadata): string {
    let text = `**Slot**: ${meta?.address || slot}`;
    text += `\n**Type**: ${value.type}\n`;
    return text;
}

function getColorTooltipString(slot: string, value: ColorLike, meta?: PropertyMetadata): string {

    let text = `**Slot**: ${meta?.address || slot}`;
    let alpha: number | undefined = undefined;
    if ("a" in value || "alpha" in value) {
        alpha = ("a" in value) ? (value as any).a : (value as any).alpha;
    }
    text += `\n**${alpha !== undefined ? "RGBA" : "RGB"}**: R=${value.r.toFixed(3)}, G=${value.g.toFixed(3)}, B=${value.b.toFixed(3)})`;
    if (alpha !== undefined) {
        text += `, A=${alpha.toFixed(3)}`;
    }
    if ("getHexString" in value && typeof value.getHexString === "function") {
        text += `\n**Hex**: #${value.getHexString()}\n`;
    }
    if ("value" in value) {
        text += `\n**Value**: ${value.value}\n`;
    }
    return text;
}


function getTextureTooltipString(slot: string, value: Texture, meta?: PropertyMetadata): string {


    let text = `**Slot**: ${meta?.address || slot}`;

    if (value.isRenderTargetTexture) text += `\n**Type**: Render Target Texture`;
    if (value.isArrayTexture) text += `\n**Type**: Array Texture`;
    if (isCubeTexture(value)) text += `\n**Type**: Cube Texture`;
    if (isCompressedTexture(value)) text += `\n**Type**: Compressed Texture`;
    if (isDataTexture(value)) text += `\n**Type**: Data Texture`;

    text += `\n**Name**: \"${value.name || ""}\"`;

    text += `\n**Format**: ${getTextureDataTypeAsString(value.type) || value.type}`;
    text += `\n**PixelFormat**: ${getTexturePixelFormatAsString(value.format) || value.format}`;

    let width = value.width || (value.image ? (value.image as any).width : undefined);
    let height = value.height || (value.image ? (value.image as any).height : undefined);
    if (width && height) {
        text += `\n**Size**: ${width.toLocaleString()} x ${height.toLocaleString()}`;
    }
    text += `\n**Mipmaps**: ${value.mipmaps ? value.mipmaps.length : 0}`;

    const memoryEstimate = estimateTextureMemory(value);
    const estimatedMemoryUsage_CPU = memoryEstimate.cpu;
    const estimatedMemoryUsage_VRAM = memoryEstimate.vram;

    if (estimatedMemoryUsage_CPU > 0)
        text += `\n**CPU Memory**: ${toHumanReadableBytes(estimatedMemoryUsage_CPU)} (${estimatedMemoryUsage_CPU.toLocaleString()} bytes)`;
    if (estimatedMemoryUsage_VRAM > 0)
        text += `\n**GPU Memory**: ${toHumanReadableBytes(estimatedMemoryUsage_VRAM)} (${estimatedMemoryUsage_VRAM.toLocaleString()} bytes)`;

    text += `\n`;
    text += `\n**WrapS**: ${value.wrapS || "unknown"}`;
    text += `\n**WrapT**: ${value.wrapT || "unknown"}`;
    text += `\n**Repeat**: ${value.repeat ? `(${value.repeat.x}, ${value.repeat.y})` : "unknown"}`;
    text += `\n**MinFilter**: ${value.minFilter || "unknown"}`;
    text += `\n**MagFilter**: ${value.magFilter || "unknown"}`;
    text += `\n**Color Space**: ${value.colorSpace || "unknown"}`;
    text += `\n**Anisotropy**: ${value.anisotropy || 0}`;

    return text;
}

function getGeometryTooltipString(slot: string, value: BufferGeometry, meta?: PropertyMetadata): string {
    let text = `**Slot**: ${slot}`;
    if (meta?.address) text += `\n**Address**: ${meta?.address}`;
    text += `\n**Type**: ${value.type}`;
    if (value.name?.length) text += `\n**Name**: \"${value.name || ""}\"`;
    text += `\n`;

    let vertices = -1;
    if (value.getAttribute("position")) {
        vertices = value.getAttribute("position").count;
    }
    else if (value.getAttribute("points")) {
        vertices = value.getAttribute("points").count;
    }

    if (typeof vertices === "number" && vertices >= 0) {
        text += `\n**Vertices**: ${vertices.toLocaleString()}`;
    }
    const indexAttr = value.getIndex?.() || value.attributes["index"];
    if (indexAttr && typeof indexAttr === "object" && "count" in indexAttr) {
        text += `\n**Indices**: ${indexAttr.count?.toLocaleString()}`;
    }

    let attributesTable = "| Attribute Name | Item Size | Offset | Count | Normalized | Usage | GPU Type |\n|---|---|---|---|---|---|---|";
    const attributes = value.attributes;
    if (attributes) {
        const attributeNames = Object.keys(attributes);
        if (attributeNames?.length > 0) {
            for (const attrName of attributeNames) {
                const attr = attributes[attrName];
                let usage = (attr as any)["usage"];
                let gpuType = (attr as any)["gpuType"] as AttributeGPUType | undefined;

                if (isInterleavedBufferAttribute(attr)) {
                    // @TODO
                    usage = attr.data.usage;
                    gpuType ||= (attr.data as any).gpuType || "-"
                }

                const offset = (attr as any)["offset"] || 0;
                let usageName = geometryAttributeUsageToString(usage);
                if (usageName === undefined) usageName = String(usage);
                let gpuTypeName = geometryAttributeGpuTypeToString(gpuType);
                if (gpuTypeName === undefined) gpuTypeName = String(gpuType);
                attributesTable += `\n| ${attrName} | ${attr.itemSize} | ${offset} | ${attr.count.toLocaleString()} | ${attr.normalized} | ${usageName} | ${gpuTypeName} |`;
                // text += `\n- ${attrName}: count=${attr.count.toLocaleString()}, itemSize=${attr.itemSize}, normalized=${attr.normalized}, usage=${(attr as any)["usage"] || "unknown"}`;
            }
        }
    }

    if (value.indirect?.count) {
        const offset = (value.indirect as any).offset || 0;
        const usage = value.indirect.usage;
        let usageName: string | undefined = geometryAttributeUsageToString(usage);
        if (usageName === undefined) usageName = String(usage);
        const gpuType = value.indirect.gpuType;
        let gpuTypeName = geometryAttributeGpuTypeToString(gpuType);
        if (gpuTypeName === undefined) gpuTypeName = String(gpuType);
        attributesTable += `\n| Indirect | ${value.indirect.itemSize} | ${offset} | ${value.indirect.count.toLocaleString()}  | ${value.indirect.normalized} | ${usageName} | ${gpuTypeName} |`;
    }

    text += `\n${attributesTable}`;
    text += `\n`;
    if (value.morphAttributes) {
        const keys = Object.keys(value.morphAttributes);
        if (keys.length > 0) {
            text += `\n**Morph Attributes**: ${keys.join(", ")}`;
            text += `\n**Relative:** ${value.morphTargetsRelative}`;
        }
    }
    text += `\n`;

    if (value.drawRange) {
        text += `\n**Draw Range**: start=${value.drawRange.start}, count=${value.drawRange.count}`;
    }

    if (value.boundingBox) {
        text += `\n**Box**: min(${toMaxFixed(value.boundingBox.min.x)}, ${toMaxFixed(value.boundingBox.min.y)}, ${toMaxFixed(value.boundingBox.min.z)}), max(${toMaxFixed(value.boundingBox.max.x)}, ${toMaxFixed(value.boundingBox.max.y)}, ${toMaxFixed(value.boundingBox.max.z)})`;
    }
    if (value.boundingSphere) {
        text += `\n**Sphere**: center(${toMaxFixed(value.boundingSphere.center.x)}, ${toMaxFixed(value.boundingSphere.center.y)}, ${toMaxFixed(value.boundingSphere.center.z)}), radius(${toMaxFixed(value.boundingSphere.radius)})`;
    }

    if (value.boundsTree) {
        text += `\n**Has BVH Bounds Tree**`;
    }


    return text;
}

function getMatrixTooltipString(slot: string, value: { elements: ArrayLike<number> }, meta?: PropertyMetadata): string {
    let text = `**Slot**: ${meta?.address || slot}`;
    const size = getMatrixDimension(value) ?? Math.sqrt(value.elements.length);
    text += `\n**Type**: Matrix${size}`;

    const elements = value.elements;
    const rows = new Array<string>();

    for (let row = 0; row < size; row++) {
        const rowValues = new Array<string>();
        for (let col = 0; col < size; col++) {
            const index = col * size + row;
            rowValues.push(toMaxFixed(elements[index]));
        }
        rows.push(`[${rowValues.join(", ")}]`);
    }

    text += `\n\n\`\`\`text\n${rows.join("\n")}\n\`\`\``;
    return text;
}


function getAcceptsForMaterialSlot(slot: string, value: any): string | undefined {
    if (isLikelyMaterialTextureInputSlot(slot, value)
    ) {
        return "null, image/*";
    }
    if (isLikelyColorInputSlot(slot, value)) {
        return "color/*";
    }
    return undefined;
}

const staticPropNames = [
    "id", "uuid", "type", "guid", "version", "needle-id"
]

const shaderMaterialKeys = new Set([
    "_shader",
    "colorWrite",
    "depthFunc",
    "depthTest",
    "depthWrite",
    "defines",
    "extensions",
    "defaultAttributeValues",
    "indirect",
    "glslVersion",
    "index0AttributeName",
    "lights",
    "precision",
    "shader",
    "shaderName",
    "uniforms",
    "uniformsGroups",
    "uniformsNeedUpdate",
    "updateRanges",
    "vertexShader",
    "fragmentShader",
]);

const infoMaterialKeys = new Set([
    "id",
    "uuid",
    "version",
    "type",
    "isMaterial",
    "isShaderMaterial",
    "userData",
    "needsUpdate",
]);

const blendingMaterialKeys = new Set([
    "blending",
    "blendEquation",
    "blendSrc",
    "blendDst",
    "blendAlpha",
    "blendEquationAlpha",
    "blendSrcAlpha",
    "blendDstAlpha",
    "blendColor",
]);

const stencilMaterialKeys = new Set([
    "stencilWrite",
    "stencilWriteMask",
    "stencilFunc",
    "stencilFuncMask",
    "stencilRef",
    "stencilMask",
    "stencilFail",
    "stencilZFail",
    "stencilZPass",
]);

const polygonOffsetMaterialKeys = new Set([
    "polygonOffset",
    "polygonOffsetFactor",
    "polygonOffsetUnits",
]);

const geometryMaterialKeys = new Set([
    "_missingTangentsWarned",
    "_needsTangents",
    "linecap",
    "linejoin",
    "missingTangentsWarned",
    "needsTangents",
    "linewidth",
    "shadowSide",
    "side",
    "vertexColors",
    "wireframe",
    "wireframeLinecap",
    "wireframeLinejoin",
    "wireframeLinewidth",
]);

const transparencyMaterialKeys = new Set([
    "opacity",
    "transparent",
    "alphaHash",
    "alphaToCoverage",
    "alphaTest",
    "dithering",
    "premultipliedAlpha",
    "forceSinglePass",
]);

const clippingMaterialKeys = new Set([
    "clipping",
    "clippingPlanes",
    "clipIntersection",
    "clipShadows",
]);

const lightingMaterialKeys = new Set([
    "envMap",
    "envMapIntensity",
    "envMapRotation",
    "flatShading",
    "fog",
    "lightMap",
    "lightMapIntensity",
    "toneMapped",
]);

function getMaterialPropertyGroup(key: string): Pick<PropertyMetadata, "detailsId" | "detailsLabel"> | null {
    if (/^is[A-Z].*Material$/.test(key)) {
        return {
            detailsId: "details.material.info",
            detailsLabel: "Info",
        };
    }

    if (key === "_shader") {
        return {
            detailsId: "details.material.shader",
            detailsLabel: "Shader",
        };
    }

    if (infoMaterialKeys.has(key)) {
        return {
            detailsId: "details.material.info",
            detailsLabel: "Info",
        };
    }

    if (shaderMaterialKeys.has(key)) {
        return {
            detailsId: "details.material.shader",
            detailsLabel: "Shader",
        };
    }

    if (lightingMaterialKeys.has(key)) {
        return {
            detailsId: "details.material.lighting",
            detailsLabel: "Lighting",
        };
    }

    if (geometryMaterialKeys.has(key)) {
        return {
            detailsId: "details.material.geometry",
            detailsLabel: "Geometry",
        };
    }

    if (key.startsWith("_")) {
        return {
            detailsId: "details.material.properties",
            detailsLabel: "Properties",
        };
    }

    if (blendingMaterialKeys.has(key)) {
        return {
            detailsId: "details.material.blending",
            detailsLabel: "Blending",
        };
    }

    if (stencilMaterialKeys.has(key)) {
        return {
            detailsId: "details.material.stencil",
            detailsLabel: "Stencil",
        };
    }

    if (polygonOffsetMaterialKeys.has(key)) {
        return {
            detailsId: "details.material.polygonOffset",
            detailsLabel: "Polygon Offset",
        };
    }

    if (transparencyMaterialKeys.has(key)) {
        return {
            detailsId: "details.material.transparency",
            detailsLabel: "Transparency",
        };
    }

    if (clippingMaterialKeys.has(key)) {
        return {
            detailsId: "details.material.clipping",
            detailsLabel: "Clipping",
        };
    }

    return null;
}

export function getMetadata(owner: object, key: string, currentValue: any): PropertyMetadata {

    const baseMetadata: PropertyMetadata = {
        priority: getPriorityValue(owner, key, currentValue),
    }

    if ("type" in owner) {
        // TODO: we either need to find the protoype that defines the type to link to the correct page
        const type = (owner as any).type;
        baseMetadata.docsUrl = `https://threejs.org/docs/?q=${key}#${type}.${key}`;
    }

    const key_lowercase = key.toLowerCase();

    // A BufferAttribute's data type and upload hint are three CONSTANTS, and the
    // inspector showed them raw: "1015" and "35044". gpuType is exactly the fact
    // that says whether a mesh is quantized — FloatType means full floats, which
    // is what Draco/meshopt leave behind after decoding. Built from three's own
    // constants so a version bump cannot silently mislabel them.
    if (isBufferAttributeLike(owner)) {
        if (key === "gpuType") {
            return {
                ...baseMetadata,
                defaultValue: THREE.FloatType,
                description: "How each component is stored on the GPU. FloatType is full precision; the integer types are what quantized (compressed) meshes use.",
                enumOptions: attributeTypeOptions,
            };
        }
        if (key === "usage") {
            return {
                ...baseMetadata,
                defaultValue: THREE.StaticDrawUsage,
                description: "The buffer's upload hint — Static for data that never changes, Dynamic for data rewritten every frame.",
                enumOptions: bufferUsageOptions,
            };
        }
    }

    if (isAnimationActionLike(owner)) {
        switch (key) {
            case "time": {
                // min+max make the number input a SLIDER — scrub the playhead
                // across the clip's duration
                let max: number | undefined;
                try { max = owner.getClip()?.duration; } catch { /* host object */ }
                if (typeof max === "number" && Number.isFinite(max) && max > 0) {
                    return {
                        ...baseMetadata,
                        min: 0, max, step: 0.01,
                        description: "Playhead time within the clip (seconds). Pause the action to scrub.",
                    };
                }
                return { ...baseMetadata, description: "Playhead time within the clip (seconds)." };
            }
            case "loop": return {
                ...baseMetadata,
                description: "Looping mode. Changing it applies from the next mixer update; a repetition COUNT additionally needs setLoop().",
                enumOptions: [
                    { label: "LoopOnce", value: 2200 },
                    { label: "LoopRepeat", value: 2201 },
                    { label: "LoopPingPong", value: 2202 },
                ],
            };
            case "weight": return {
                ...baseMetadata,
                min: 0, max: 1, step: 0.01, defaultValue: 1,
                description: "Blend weight of this action (0–1). Multiple running actions mix by weight.",
            };
        }
    }
    if (isAnimationClipLike(owner) && key === "blendMode") {
        return {
            ...baseMetadata,
            description: "How this clip combines with others when played.",
            enumOptions: [
                { label: "NormalAnimationBlendMode", value: 2500 },
                { label: "AdditiveAnimationBlendMode", value: 2501 },
            ],
        };
    }

    if (key === "attributes" && isGeometry(owner) && currentValue && typeof currentValue === "object") {
        const attributeNames = Object.keys(currentValue).filter(Boolean);
        if (attributeNames.length > 0) {
            return {
                ...baseMetadata,
                objectValuePlaceholderString: attributeNames.join(", "),
            };
        }
    }

    if (key === "index") {
        const topologyPlaceholder = getGeometryIndexPlaceholder(owner, currentValue);
        if (topologyPlaceholder) {
            return {
                ...baseMetadata,
                objectValuePlaceholderString: topologyPlaceholder,
            };
        }
    }

    if (key === "drawRange" && currentValue && typeof currentValue === "object") {
        const drawRangeLike = currentValue as { start?: unknown; count?: unknown };
        const start = typeof drawRangeLike.start === "number" ? drawRangeLike.start : Number.NaN;
        const count = typeof drawRangeLike.count === "number" ? drawRangeLike.count : Number.NaN;
        if (Number.isFinite(start)) {
            return {
                ...baseMetadata,
                objectValuePlaceholderString: formatRangeSummary(start, count),
            };
        }
    }

    if (key === "boundingBox") {
        const placeholder = getBoundingBoxPlaceholder(currentValue);
        if (placeholder) {
            return {
                ...baseMetadata,
                objectValuePlaceholderString: placeholder,
            };
        }
    }

    if (key === "boundingSphere") {
        const placeholder = getBoundingSpherePlaceholder(currentValue);
        if (placeholder) {
            return {
                ...baseMetadata,
                objectValuePlaceholderString: placeholder,
            };
        }
    }

    if (Array.isArray(owner) && currentValue && typeof currentValue === "object") {
        const groupLike = currentValue as { start?: unknown; count?: unknown; materialIndex?: unknown };
        const start = typeof groupLike.start === "number" ? groupLike.start : Number.NaN;
        const count = typeof groupLike.count === "number" ? groupLike.count : Number.NaN;
        const materialIndex = typeof groupLike.materialIndex === "number" ? groupLike.materialIndex : Number.NaN;
        if (Number.isFinite(start) && Number.isFinite(count) && Number.isFinite(materialIndex)) {
            return {
                ...baseMetadata,
                objectValuePlaceholderString: `${formatRangeSummary(start, count)}, material ${materialIndex}`,
            };
        }
    }

    if (currentValue && typeof currentValue === "object") {
        const bufferAttr = currentValue as { isBufferAttribute?: boolean; isInterleavedBufferAttribute?: boolean; count?: unknown };
        if (bufferAttr.isBufferAttribute === true || bufferAttr.isInterleavedBufferAttribute === true) {
            const count = typeof bufferAttr.count === "number" ? bufferAttr.count : Number.NaN;
            if (Number.isFinite(count)) {
                return {
                    ...baseMetadata,
                    objectValuePlaceholderString: count === 1 ? "1 item" : `${count} items`,
                };
            }
        }
    }

    // #region Renderer metadata
    if (isRenderer(owner)) {
        baseMetadata.address = `renderer/${key}`;
        baseMetadata.contextKey = `renderer.${key}`;
        switch (key) {
            case "outputEncoding": {
                return {
                    ...baseMetadata,
                    description: "The encoding of the renderer's output. This affects how colors are displayed on the screen.",
                    enumOptions: [
                        { label: "LinearEncoding", value: 3000 },
                        { label: "sRGBEncoding", value: 3001 },
                    ]
                };
            }
            case "toneMapping": {
                return {
                    ...baseMetadata,
                    description: "The tone mapping algorithm to use when rendering.",
                    enumOptions: [
                        { label: "NoToneMapping", value: 0 },
                        { label: "LinearToneMapping", value: 1 },
                        { label: "ReinhardToneMapping", value: 2 },
                        { label: "CineonToneMapping", value: 3 },
                        { label: "ACESFilmicToneMapping", value: 4 },
                        { label: "CustomToneMapping", value: 5 },
                        { label: "AgXToneMapping", value: 6 },
                        { label: "NeutralToneMapping", value: 7 },
                    ]
                };
            }
            case "toneMappingExposure": {
                return {
                    ...baseMetadata,
                    min: 0, max: 10, step: 0.1, defaultValue: 1, description: "The exposure level when tone mapping is applied."
                };
            }
            case "coordinateSystem": {
                return {
                    ...baseMetadata,
                    static: true,
                    description: "The coordinate system used by the renderer.",
                    enumOptions: [
                        { label: "WebGL CoordinateSystem", value: 2000 },
                        { label: "WebGPU CoordinateSystem", value: 2001 },
                    ]
                };
            }
            case "outputColorSpace": {
                return {
                    ...baseMetadata,
                    description: "The color space of the renderer's output.",
                    enumOptions: [
                        { label: "SRGBColorSpace", value: "srgb" },
                        { label: "LinearSRGBColorSpace", value: "srgb-linear" },
                    ]
                };
            }
            case "gammaFactor": {
                return {
                    ...baseMetadata,
                    min: 0, max: 5, step: 0.01, defaultValue: 1,
                    description: "The gamma factor for the renderer."
                };
            }
            case "transmissionResolutionScale": {
                return {
                    ...baseMetadata,
                    min: 0.1, max: 2, step: 0.1, defaultValue: 1,
                    description: "The scale factor for transmission render targets."
                };
            }
            case "clippingPlanes": {
                return {
                    ...baseMetadata,
                    description: `User-defined clipping planes specified in world space. These planes apply globally. Points in space whose dot product with the plane is negative are cut away.
![clipping planes](http://cdn.needle.tools/static/images/fun/homer-bush.gif)`
                }
            }
        }
    }

    // #region Object3D metadata
    if (isObject3D(owner)) {
        baseMetadata.address = `${handleOf(owner) ?? owner.id}/${key}`;
        baseMetadata.contextKey = `object.${key}`;
        switch (key) {
            case "type": {
                let description = "The object's type (e.g. if it's a Mesh or Light or Camera...)";
                let docsUrl = baseMetadata.docsUrl;
                if (currentValue === "Scene") {
                    description = `Three.js's core is arguably its scene graph. A scene graph in a 3D engine is a hierarchy of nodes in a graph where each node represents a local space. 
![](https://threejs.org/manual/resources/images/scenegraph-generic.svg)
### Here is an example of how local spaces work:
The Earth orbits the Sun. The Moon orbits the Earth. The Moon moves in a circle around the Earth. From the Moon's point of view it's rotating in the "local space" of the Earth. Even though its motion relative to the Sun is some crazy spirograph like curve from the Moon's point of view it just has to concern itself with rotating around the Earth's local space.

[Further reading](https://threejs.org/manual/#en/scenegraph)`;
                }
                return {
                    ...baseMetadata,
                    static: true,
                    description: description,
                    docsUrl: docsUrl,
                };
            }
            case "name": {
                return {
                    ...baseMetadata,
                    description: "The name of the object.",
                };
            }
            case "visible": {
                return {
                    ...baseMetadata,
                    defaultValue: true,
                    description: "When set to true, the 3D object gets rendered.",
                };
            }
            case "castShadow": {
                return {
                    ...baseMetadata,
                    defaultValue: false,
                    description: "Whether the object casts shadows.",
                };
            }
            case "receiveShadow": {
                return {
                    ...baseMetadata,
                    defaultValue: false,
                    description: "Whether the object receives shadows.",
                };
            }
            case "renderOrder": {
                return {
                    ...baseMetadata,
                    min: -100, max: 100, step: 1,
                    defaultValue: 0,
                    docsUrl: "https://threejs.org/docs/#Object3D.renderOrder",
                    description: "**This value allows the default rendering order of scene graph objects to be overridden** although opaque and transparent objects remain sorted independently. When this property is set for an instance of Group, all descendants objects will be sorted and rendered together. Sorting is from lowest to highest render order."
                };
            }
            case "userData": {
                return {
                    ...baseMetadata,
                    description: "Custom user data associated with this object. Userdata will be copied when an object is cloned. It should not hold references to functions as these will not be cloned."
                };
            }
            case "coordinateSystem": {
                return {
                    ...baseMetadata,
                    static: true,
                    description: "The coordinate system used by this object.", enumOptions: [
                        { label: "WebGL CoordinateSystem", value: 2000 },
                        { label: "WebGPU CoordinateSystem", value: 2001 },
                    ]
                };
            }
            case "layers": {
                return {
                    ...baseMetadata,
                    description: "The layer mask of this object."
                };
            }
            case "geometry": {
                if (isGeometry(currentValue)) {
                    let placeholderString = "";
                    // check if "hasAttribute" function exists
                    if (currentValue.attributes) {
                        if ("hasAttribute" in currentValue) {
                            if (currentValue.hasAttribute("position")) {
                                placeholderString += `${toHumanReadableCount(currentValue.getAttribute("position")?.count ?? 0)} vertices\n`;
                            }
                            else if (currentValue.getAttribute("points")) {
                                placeholderString += `${toHumanReadableCount(currentValue.getAttribute("points")?.count ?? 0)} points\n`;
                            }
                            else if (currentValue.getAttribute("index")) {
                                placeholderString += `${toHumanReadableCount((currentValue.getAttribute("index")?.count ?? 0) / 3)} faces\n`;
                            }
                        }
                    }
                    return {
                        ...baseMetadata,
                        description: "The geometry of this object.",
                        objectValuePlaceholderString: placeholderString,
                    }
                }
            }
            case "skeleton": {
                if (isSkeleton(currentValue)) {
                    const bonescount = currentValue.bones.length;
                    return {
                        ...baseMetadata,
                        description: `Skeleton with ${currentValue.bones.length} bones.`,
                        objectValuePlaceholderString: `${bonescount} bones`,
                    }
                }
            }
            case "scale": {
                baseMetadata.constrainedProportions = true;
            }
        }
    }

    if (isMeshLike(owner)) {
        switch (key) {
            case "material":
                baseMetadata.picker = {
                    id: `material.${owner.id}`,
                    prefix: PropertyPicker.PREFIX + " material",
                }
                break;
        }
    }

    if (isInstancedMeshLike(owner)) {
        switch (key) {
            case "count": {
                return {
                    ...baseMetadata,
                    description: "The number of instances.",
                    docsUrl: "https://threejs.org/docs/?q=instanced#InstancedMesh.count"
                }
            }
        }
    }
    else if (isMeshLike(owner)) {
        switch (key) {
            case "count": { // https://threejs.org/docs/?q=instanced#Mesh.count
                return {
                    ...baseMetadata,
                    description: "The number of instances of this mesh. Can only be used with WebGPURenderer."
                }
            }
        }
    }

    if (isSkinnedMeshLike(owner)) {
        switch (key) {
            case "bindMode": {
                return {
                    ...baseMetadata,
                    description: "How the skeleton is bound to the skinned mesh.",
                    enumOptions: [
                        {
                            label: "Attached",
                            description: "The skeleton is attached to the skinned mesh and will follow its transformations.",
                            value: "attached",
                        },
                        {
                            label: "Detached",
                            description: "The skeleton is detached from the skinned mesh and will not follow its transformations.",
                            value: "detached"
                        },
                    ]
                }
            }
        }
    }

    // #region material_metadata
    if (isMaterial(owner)) {

        let accepts: PropertyMetadata["accept"] = getAcceptsForMaterialSlot(key, currentValue);
        const materialGroupMeta = getMaterialPropertyGroup(key);

        if (key.startsWith("is") && key.endsWith("Material")) {
            return {
                ...baseMetadata,
                ...(materialGroupMeta ?? {}),
                accept: accepts, static: true, description: `Whether this material is a ${key.substring(2)} `
            };
        }

        // TODO: we need the object id
        baseMetadata.address = `material/${key}`;
        baseMetadata.contextKey = `material.${key}`;
        if (accepts) {
            baseMetadata.accept = accepts;
        }
        if (materialGroupMeta) {
            baseMetadata.detailsId = materialGroupMeta.detailsId;
            baseMetadata.detailsLabel = materialGroupMeta.detailsLabel;
        }

        switch (key) {
            case "type": {
                return {
                    ...baseMetadata,
                    accept: accepts,
                    static: true,
                    description: "The material's type."
                };
            }
            case "vertexShader":
            case "fragmentShader": {
                return {
                    ...baseMetadata,
                    accept: accepts,
                    description: "The GLSL code for the " + (key === "vertexShader" ? "vertex" : "fragment") + " shader."
                };
            }
            case "needsUpdate": {
                return {
                    ...baseMetadata,
                    accept: accepts,
                    description: "Set to true to indicate that the material needs to be recompiled."
                };
            }
            case "version": {
                return {
                    ...baseMetadata,
                    accept: accepts,
                    description: "The material version. This is incremented each time the material is changed."
                };
            }
            case "clippingPlanes": {
                return {
                    ...baseMetadata,
                    description: `User-defined clipping planes specified as THREE.Plane objects in world space. These planes apply to the objects this material is attached to. Points in space whose signed distance to the plane is negative are clipped (not rendered). This requires [WebGLRenderer#localClippingEnabled](https://threejs.org/docs/pages/WebGLRenderer.html#localClippingEnabled) to be true.

![clipping planes](http://cdn.needle.tools/static/images/fun/homer-bush.gif)`
                };
            }
            case "side": {
                return {
                    ...baseMetadata,
                    min: 0, max: 2, step: 1, defaultValue: 0, description: "0: FrontSide, 1: BackSide, 2: DoubleSide",
                    enumOptions: [
                        { label: "FrontSide", value: 0 },
                        { label: "BackSide", value: 1 },
                        { label: "DoubleSide", value: 2 },
                    ]
                };
            }
            case "shadowSide": {
                return {
                    ...baseMetadata,
                    min: 0, max: 2, step: 1,
                    defaultValue: null,
                    description: `Defines which side of faces cast shadows.If null, the side casting shadows is determined as follows:
        - When Material#side is set to FrontSide, the back side cast shadows.
- When Material#side is set to BackSide, the front side cast shadows.
- When Material#side is set to DoubleSide, both sides cast shadows.`,
                    enumOptions: [
                        { label: "FrontSide (default)", value: null },
                        { label: "FrontSide", value: 0 },
                        { label: "BackSide", value: 1 },
                        { label: "DoubleSide", value: 2 },
                    ]
                };
            }
            case "precision": {
                return {
                    ...baseMetadata,
                    min: -1, max: 2, step: 1, defaultValue: null,
                    description: `Override the renderer's default precision for this material. By default the renderer's default precision is used. 

0: highp: 32 - bit floating point precision.Provides the highest accuracy for complex calculations and detailed graphics, but consumes more GPU resources.Best for materials requiring precise mathematical operations or fine visual detail.

1: mediump: 16 - bit floating point precision.Balances visual quality with performance, suitable for most standard materials and effects.Offers good accuracy while being more efficient than high precision on mobile and lower - end devices.

2: lowp: 10 - bit floating point precision.Maximum performance with reduced accuracy.Ideal for simple materials, color values, or non - critical calculations where visual differences are negligible.Commonly used for mobile optimization.`,
                    enumOptions: [
                        { label: "Renderer (default)", value: null },
                        { label: "highp", value: "highp" },
                        { label: "mediump", value: "mediump" },
                        { label: "lowp", value: "lowp" },
                    ]
                };
            }

            case "vertexColors": {
                return {
                    ...baseMetadata,
                    defaultValue: false,
                    description: "Whether vertex colors are used."
                }
            }

            case "map": {
                return {
                    ...baseMetadata,
                    defaultValue: null,
                    description: "The primary texture map that drives this material's surface color.",
                };
            }

            case "dithering": {
                return {
                    ...baseMetadata,
                    defaultValue: false,
                    description: "Whether to apply dithering to the material to reduce color banding."
                };
            }
            case "premultipliedAlpha": {
                return {
                    ...baseMetadata,
                    defaultValue: false,
                    when: ContextKeyExpr.equals("material.transparent", true),
                    description: "Whether the alpha channel is premultiplied."
                };
            }

            case "normalMapType": {
                return {
                    ...baseMetadata,
                    when: ContextKeyExpr.notEquals("material.normalMap", null),
                    accept: accepts, min: 0, max: 1, step: 1, defaultValue: 0, description: `0: TangentSpaceNormalMap,
1: ObjectSpaceNormalMap`,
                    enumOptions: [
                        { label: "TangentSpaceNormalMap", value: 0 },
                        { label: "ObjectSpaceNormalMap", value: 1 },
                    ]
                };
            }
            case "normalScale": {
                return {
                    ...baseMetadata,
                    when: ContextKeyExpr.notEquals("material.normalMap", null),
                    description: "How much the normal map affects the material. A value of (1, 1) means the normal map has full effect, while (0, 0) means it has no effect."
                };
            }
            case "depthTest": {
                return {
                    ...baseMetadata,
                }
            }
            case "depthFunc": {
                return {
                    ...baseMetadata,
                    inset: true,
                    when: ContextKeyExpr.equals("material.depthTest", true),
                    accept: accepts, min: 0, max: 8, step: 1, defaultValue: 3, description: `0: NeverDepth,
1: AlwaysDepth: Always pass
2: LessDepth: Pass if the incoming value is less than the depth buffer value.
3: LessEqualDepth: Pass if the incoming value is less than or equal to the depth buffer value.
4: EqualDepth: Pass if the incoming value is equal to the depth buffer value.
5: GreaterEqualDepth: Pass if the incoming value is greater than or equal to the depth buffer value.
6: GreaterDepth: Pass if the incoming value is greater than the depth buffer value.
7: NotEqualDepth: Pass if the incoming value is not equal to the depth buffer value. 
8: AlwaysDepth: Always pass`,
                    enumOptions: [
                        { label: "NeverDepth", value: 0 },
                        { label: "AlwaysDepth", value: 1 },
                        { label: "LessDepth", value: 2 },
                        { label: "LessEqualDepth", value: 3 },
                        { label: "EqualDepth", value: 4 },
                        { label: "GreaterEqualDepth", value: 5 },
                        { label: "GreaterDepth", value: 6 },
                        { label: "NotEqualDepth", value: 7 },
                        { label: "AlwaysDepth", value: 8 },
                    ]
                };
            }

            case "alphaToCoverage": {
                return {
                    ...baseMetadata,
                    when: ContextKeyExpr.equals("material.transparent", false),
                    description: "Uses MSAA sample coverage to simulate transparency without sorting issues. Requires antialias enabled and transparent: false.",

                };
            }
            case "alphaHash": {
                return {
                    ...baseMetadata,
                    description: "Whether to use alpha hashing for transparency. This is an alternative to alpha blending that does not require sorting. See the three.js [Alpha Hashing example](https://threejs.org/examples/#webgl_materials_alphahash) to learn more.",
                }
            }
            case "transparent": {
                return {
                    ...baseMetadata,
                };
            }
            case "opacity": {
                return {
                    ...baseMetadata,
                    when: ContextKeyExpr.or(
                        ContextKeyExpr.equals("material.transparent", true),
                        ContextKeyExpr.equals("material.alphaHash", true),
                        ContextKeyExpr.greater("material.transmission", 0), // when transmission is not zero opacity should be set to 1
                    ),
                    accept: accepts,
                    min: 0, max: 1, step: 0.01, defaultValue: 1
                };
            }
            case "_alphaTest":
            case "alphaTest": {
                return {
                    ...baseMetadata,
                    when: ContextKeyExpr.or(
                        ContextKeyExpr.equals("material.transparent", true),
                        ContextKeyExpr.equals("material.alphaHash", true),
                    ),
                    accept: accepts, min: 0, max: 1, step: 0.01, defaultValue: 0
                };
            }
            case "roughness": {
                return {
                    ...baseMetadata,
                    accept: accepts, min: 0, max: 1, step: 0.01,
                    defaultValue: 1,
                    description: "How rough the material appears. 0.0 means a smooth mirror reflection, 1.0 means fully diffuse. If roughnessMap is also provided, both values are multiplied."
                };
            }
            case "metalness": {
                return {
                    ...baseMetadata,
                    accept: accepts, min: 0, max: 1, step: 0.01,
                    defaultValue: 0,
                    description: "How much the material is like a metal. Non-metallic materials such as wood or stone use 0.0, metallic use 1.0, with nothing (usually) in between. A value between 0.0 and 1.0 could be used for a rusty metal look. If metalnessMap is also provided, both values are multiplied."
                };
            }
            case "aoMapIntensity": {
                return {
                    ...baseMetadata,
                    when: ContextKeyExpr.equals("material.aoMap", true),
                    accept: accepts, min: 0, max: 10, step: 0.1, defaultValue: 1
                };
            }
            case "lightMapIntensity": {
                return {
                    ...baseMetadata,
                    when: ContextKeyExpr.notEquals("material.lightMap", null),
                    accept: accepts, min: 0, max: 10, step: 0.1, defaultValue: 1
                };
            }
            case "emissive": {
                return {
                    ...baseMetadata,
                    accept: accepts,
                };
            }
            case "emissiveIntensity": {
                return {
                    ...baseMetadata,
                    when: ContextKeyExpr.notEquals("material.emissive", 0),
                    accept: accepts, min: 0, max: 3, step: 0.1,
                    defaultValue: 1,
                };
            }
            case "envMap": {
                return {
                    ...baseMetadata,
                    accept: accepts,
                    defaultValue: null,
                    picker: {
                        id: `envMap.${(owner as any).id}`,
                        prefix: PropertyPicker.PREFIX + " hdri",
                    }
                };
            }
            case "envMapIntensity": {
                return {
                    ...baseMetadata,
                    when: ContextKeyExpr.notEquals("material.envMap", null),
                    accept: accepts, min: 0, max: 10, step: 0.1, defaultValue: 1
                };
            }
            case "envMapRotation": {
                return {
                    ...baseMetadata,
                    when: ContextKeyExpr.notEquals("material.envMap", null),
                };
            }
            case "bumpScale": {
                return {
                    ...baseMetadata,
                    when: ContextKeyExpr.equals("material.bumpMap", true),
                    accept: accepts, min: 0, max: 2, step: 0.01, defaultValue: 1
                };
            }

            // #region anisotropy

            case "anisotropy": {
                return {
                    ...baseMetadata,
                    accept: accepts, min: 0, max: 1, step: .01, defaultValue: 0
                };
            }
            case "anisotropyMap": {
                return {
                    ...baseMetadata,
                    accept: accepts,
                    defaultValue: null,
                    description: "Red and green channels represent the anisotropy direction in [-1, 1] tangent, bitangent space, to be rotated by anisotropyRotation. The blue channel contains strength as [0, 1] to be multiplied by anisotropy.",
                };
            }
            case "anisotropyRotation": {
                return {
                    ...baseMetadata,
                    accept: accepts, min: 0, max: Math.PI * 2, step: 0.01,
                    defaultValue: 1,
                    description: "The rotation of the anisotropy in tangent, bitangent space, measured in radians counter-clockwise from the tangent. When anisotropyMap is present, this property provides additional rotation to the vectors in the texture",
                };
            }

            // #region attenuation
            case "attenuationColor": {
                return {
                    ...baseMetadata,
                    accept: accepts, description: "The color that white light turns into due to absorption when reaching the attenuation distance.",
                    defaultValue: 0xffffff
                }
            }
            case "attenuationDistance": {
                return {
                    ...baseMetadata,
                    accept: accepts,
                    min: 0, max: Infinity, step: 0.01,
                    defaultValue: Infinity,
                    description: "Density of the medium given as the average distance that light travels in the medium before interacting with a particle. The value is given in world space units, and must be greater than zero.",
                }
            }

            // #region clearcoat
            case "clearcoat": {
                return {
                    ...baseMetadata,
                    min: 0, max: 1, step: 0.01,
                    defaultValue: 0,
                    description: "Represents the intensity of the clear coat layer, from 0.0 to 1.0. Use clear coat related properties to enable multilayer materials that have a thin translucent layer over the base layer."
                }
            }
            case "clearcoatMap": {
                return {
                    ...baseMetadata,
                    accept: accepts,
                    defaultValue: null,
                    description: "The red channel of this texture is multiplied against clearcoat, for per-pixel control over a coating's intensity."
                }
            }
            case "clearcoatNormalMap": {
                return {
                    ...baseMetadata,
                    accept: accepts,
                    defaultValue: null,
                    description: "Can be used to enable independent normals for the clear coat layer",
                };
            }
            case "clearcoatNormalScale": {
                return {
                    ...baseMetadata,
                    defaultValue: { x: 1, y: 1 },
                    description: "How much clearcoatNormalMap affects the clear coat layer, from (0,0) to (1,1)."
                }
            }
            case "clearcoatRoughness": {
                return {
                    ...baseMetadata,
                    min: 0, max: 1, step: 0.01,
                    defaultValue: 0,
                    description: "Roughness of the clear coat layer, from 0.0 to 1.0."
                }
            }
            case "clearcoatRoughnessMap": {
                return {
                    ...baseMetadata,
                    accept: accepts,
                    defaultValue: null,
                    description: "The green channel of this texture is multiplied against clearcoatRoughness, for per-pixel control over a coating's roughness."
                }
            }

            // #region transmission
            case "dispersion": {
                return {
                    ...baseMetadata,
                    accept: accepts, min: 0, max: 1, step: 0.01, defaultValue: 0, description: "Defines the strength of the angular separation of colors (chromatic aberration) transmitting through a relatively clear volume. Any value zero or larger is valid, the typical range of realistic values is [0, 1]. This property can be only be used with transmissive objects."
                }
            }
            case "ior": {
                return {
                    ...baseMetadata,
                    accept: accepts, min: 1, max: 3, step: 0.01,
                    defaultValue: 1.5,
                    description: "Index-of-refraction for non-metallic materials, from 1.0 to 2.333."
                }
            }
            case "thickness": {
                return {
                    ...baseMetadata,
                    accept: accepts, min: 0, max: 10, step: 0.01,
                    defaultValue: 0,
                    description: "The thickness of the volume beneath the surface. The value is given in the coordinate space of the mesh. If the value is 0 the material is thin-walled. Otherwise the material is a volume boundary."
                }
            }
            case "thicknessMap": {
                return {
                    ...baseMetadata,
                    accept: accepts,
                    defaultValue: null,
                    description: "A texture that defines the thickness, stored in the green channel. This will be multiplied by thickness."
                }
            }
            case "transmission": {
                return {
                    ...baseMetadata,
                    accept: accepts, min: 0, max: 1, step: 0.01,
                    defaultValue: 0,
                    description: `Degree of transmission(or optical transparency), from 0.0 to 1.0.

            Thin, transparent or semitransparent, plastic or glass materials remain largely reflective even if they are fully transmissive.The transmission property can be used to model these materials.

When transmission is non - zero, opacity should be set to 1.`
                }
            }
            case "transmissionMap": {
                return {
                    ...baseMetadata,
                    accept: accepts,
                    defaultValue: null,
                    description: "The red channel of this texture is multiplied against transmission, for per-pixel control over optical transparency."
                }
            }

            // #region iridescence

            case "iridescence": {
                return {
                    ...baseMetadata,
                    min: 0, max: 1, step: 0.01,
                    defaultValue: 0,
                    description: "The intensity of the iridescence layer, simulating RGB color shift based on the angle between the surface and the viewer, from 0.0 to 1.0."
                }
            }
            case "iridescenceIOR": {
                return {
                    ...baseMetadata,
                    when: ContextKeyExpr.greater("material.iridescence", 0),
                    min: 1, max: 2.333, step: 0.01,
                    defaultValue: 1.3,
                    description: "Strength of the iridescence RGB color shift effect, represented by an index-of-refraction. Between 1.0 to 2.333."
                }
            }
            case "iridescenceMap": {
                return {
                    ...baseMetadata,
                    accept: accepts,
                    defaultValue: null,
                    description: "The red channel of this texture is multiplied against iridescence, for per-pixel control over iridescence."
                }
            }
            case "iridescenceThicknessMap": {
                return {
                    ...baseMetadata,
                    accept: accepts,
                    defaultValue: null,
                    description: `A texture that defines the thickness of the iridescence layer, stored in the green channel.Minimum and maximum values of thickness are defined by iridescenceThicknessRange array:
        - 0.0 in the green channel will result in thickness equal to first element of the array. 
- 1.0 in the green channel will result in thickness equal to second element of the array.
        Values in -between will linearly interpolate between the elements of the array.`
                }
            }
            case "iridescenceThicknessRange": {
                return {
                    ...baseMetadata,
                    // when: ContextKeyExpr.greater("material.iridescence", 0),
                    defaultValue: [100, 400],
                    description: "Array of exactly 2 elements, specifying minimum and maximum thickness of the iridescence layer. Thickness of iridescence layer has an equivalent effect of the one thickness has on ior."
                }
            }

            // #region physical 
            case "reflectivity": {
                return {
                    ...baseMetadata,
                    accept: accepts, min: 0, max: 1, step: 0.01, defaultValue: 0.5,
                    description: `Degree of reflectivity, from 0.0 to 1.0.Default is 0.5, which corresponds to an index - of - refraction of 1.5. 
This models the reflectivity of non - metallic materials.It has no effect when metalness is 1.0.`
                }
            }

            // #region sheen
            case "sheen": {
                return {
                    ...baseMetadata,
                    min: 0, max: 1, step: 0.01,
                    defaultValue: 0,
                    description: "The intensity of the sheen layer, from 0.0 to 1.0."
                }
            }
            case "sheenColor": {
                return {
                    ...baseMetadata,
                    accept: accepts,
                    description: "The sheen tint.",
                    defaultValue: 0x000000
                }
            }
            case "sheenColorMap": {
                return {
                    ...baseMetadata,
                    accept: accepts,
                    defaultValue: null,
                    description: "The RGB channels of this texture are multiplied against sheenColor, for per-pixel control over sheen tint."
                }
            }
            case "sheenRoughness": {
                return {
                    ...baseMetadata,
                    min: 0, max: 1, step: 0.01,
                    defaultValue: 1,
                    description: "Roughness of the sheen layer, from 0.0 to 1.0."
                }
            }
            case "sheenRoughnessMap": {
                return {
                    ...baseMetadata,
                    accept: accepts,
                    defaultValue: null,
                    description: "The alpha channel of this texture is multiplied against sheenRoughness, for per-pixel control over sheen roughness."
                }
            }

            // #region specular
            case "specularColor": {
                return {
                    ...baseMetadata,
                    accept: accepts,
                    description: "Tints the specular reflection at normal incidence for non-metals only",
                    defaultValue: 0xffffff
                }
            }
            case "specularColorMap": {
                return {
                    ...baseMetadata,
                    accept: accepts,
                    defaultValue: null,
                    description: "The RGB channels of this texture are multiplied against specularColor, for per-pixel control over specular color."
                }
            }
            case "specularIntensity": {
                return {
                    ...baseMetadata,
                    accept: accepts, min: 0, max: 1, step: 0.01, defaultValue: 1,
                    description: "A float that scales the amount of specular reflection for non-metals only. When set to zero, the model is effectively Lambertian. From 0.0 to 1.0."
                }
            }
            case "specularIntensityMap": {
                return {
                    ...baseMetadata,
                    accept: accepts,
                    defaultValue: null,
                    description: "The alpha channel of this texture is multiplied against specularIntensity, for per-pixel control over specular intensity."
                };
            }


            // #region displacement
            case "displacementScale": {
                return {
                    ...baseMetadata,
                    when: ContextKeyExpr.equals("material.displacementMap", true),
                    accept: accepts,
                    min: 0, max: 10, step: 0.1, defaultValue: 1
                };
            }
            case "displacementBias": {
                return {
                    ...baseMetadata,
                    when: ContextKeyExpr.equals("material.displacementMap", true),
                    accept: accepts, min: -5, max: 5, step: 0.1, defaultValue: 0
                };
            }

            // #region polygon offset
            case "polygonOffset": {
                return {
                    ...baseMetadata,
                    accept: accepts, description: "Whether to use polygon offset or not. When enabled, each fragment's depth value will be offset after it is interpolated from the depth values of the appropriate vertices. The offset is added before the depth test is performed and before the value is written into the depth buffer. \n\nCan be useful for rendering hidden-line images, for applying decals to surfaces, and for rendering solids with highlighted edges."
                };
            }

            case "polygonOffsetFactor": {
                return {
                    ...baseMetadata,
                    inset: true,
                    when: ContextKeyExpr.equals(`material.polygonOffset`, true),
                    accept: accepts, min: -100, max: 100, step: 1, defaultValue: 0, description: "Specifies a scale factor that is used to create a variable depth offset for each polygon."
                };
            }
            case "polygonOffsetUnits": {
                return {
                    ...baseMetadata,
                    inset: true,
                    when: ContextKeyExpr.equals(`material.polygonOffset`, true),
                    accept: accepts, min: -100, max: 100, step: 1, defaultValue: 0, description: "Is multiplied by an implementation-specific value to create a constant depth offset."
                };
            }


            // #region blending
            case "blending": {
                return {
                    ...baseMetadata,
                    min: 0, max: 5, step: 1, defaultValue: 1,
                    description: `Defines the blending type of the material. It must be set to CustomBlending if custom blending properties like Material#blendSrc, Material#blendDst or Material#blendEquation should have any effect.

See the three.js [Materials Blending](https://threejs.org/examples/#webgl_materials_blending) and [Custom Blending](https://threejs.org/examples/#webgl_materials_blending_custom) examples for all the available options.`,
                    enumOptions: [
                        { label: "No Blending", value: 0 },
                        { label: "Normal Blending", value: 1 },
                        { label: "Additive Blending", value: 2 },
                        { label: "Subtractive Blending", value: 3 },
                        { label: "Multiply Blending", value: 4 },
                        { label: "Custom Blending", value: 5 },
                    ]
                };
            }
            case "blendEquation": {
                // https://threejs.org/docs/#Material.blendEquation
                return {
                    ...baseMetadata,
                    inset: true,
                    when: ContextKeyExpr.equals("material.blending", 5), // CustomBlending
                    description: "Defines how the blending is calculated. Default is AddEquation.",
                    enumOptions: [
                        { label: "AddEquation", value: 100 },
                        { label: "SubtractEquation", value: 101 },
                        { label: "ReverseSubtractEquation", value: 102 },
                        { label: "MinEquation", value: 103 },
                        { label: "MaxEquation", value: 104 },
                    ]
                };
            }

            case "blendSrc":
            case "blendDst": {
                // ZeroFactor | OneFactor | SrcColorFactor | OneMinusSrcColorFactor | SrcAlphaFactor | OneMinusSrcAlphaFactor | DstAlphaFactor | OneMinusDstAlphaFactor | DstColorFactor | OneMinusDstColorFactor | SrcAlphaSaturateFactor | ConstantColorFactor | OneMinusConstantColorFactor | ConstantAlphaFactor | OneMinusConstantAlphaFactor
                return {
                    ...baseMetadata,
                    inset: true,
                    when: ContextKeyExpr.equals("material.blending", 5), // CustomBlending
                    description: "Defines the blending source factor.",
                    enumOptions: [
                        { label: "ZeroFactor", value: 200 },
                        { label: "OneFactor", value: 201 },
                        { label: "SrcColorFactor", value: 202 },
                        { label: "OneMinusSrcColorFactor", value: 203 },
                        { label: "SrcAlphaFactor", value: 204 },
                        { label: "OneMinusSrcAlphaFactor", value: 205 },
                        { label: "DstAlphaFactor", value: 206 },
                        { label: "OneMinusDstAlphaFactor", value: 207 },
                        { label: "DstColorFactor", value: 208 },
                        { label: "OneMinusDstColorFactor", value: 209 },
                        { label: "SrcAlphaSaturateFactor", value: 210 },
                        { label: "ConstantColorFactor", value: 211 },
                        { label: "OneMinusConstantColorFactor", value: 212 },
                        { label: "ConstantAlphaFactor", value: 213 },
                        { label: "OneMinusConstantAlphaFactor", value: 214 },
                    ]
                }
            }
            case "blendAlpha": {
                return {
                    ...baseMetadata,
                    inset: true,
                    when: ContextKeyExpr.equals("material.blending", 5), // CustomBlending
                    accept: accepts, min: 0, max: 1, step: 0.01,
                    defaultValue: 0,
                };
            }
            case "blendColor": {
                return {
                    ...baseMetadata,
                    inset: true,
                    when: ContextKeyExpr.equals("material.blending", 5), // CustomBlending
                    accept: accepts,
                    defaultValue: 0x000000,
                };
            }

            case "blendEquationAlpha": {
                // https://threejs.org/docs/#Material.blendEquationAlpha
                return {
                    ...baseMetadata,
                    inset: true,
                    when: ContextKeyExpr.equals("material.blending", 5), // CustomBlending
                    description: "Defines the blending equation of the alpha channel. Default is null.",
                    enumOptions: [
                        { label: "AddEquation", value: 100 },
                        { label: "SubtractEquation", value: 101 },
                        { label: "ReverseSubtractEquation", value: 102 },
                        { label: "MinEquation", value: 103 },
                        { label: "MaxEquation", value: 104 },
                        { label: "Default", value: null },
                    ]
                };
            }
            case "blendSrcAlpha":
            case "blendDstAlpha": {
                // ZeroFactor | OneFactor | SrcColorFactor | OneMinusSrcColorFactor | SrcAlphaFactor | OneMinusSrcAlphaFactor | DstAlphaFactor | OneMinusDstAlphaFactor | DstColorFactor | OneMinusDstColorFactor | SrcAlphaSaturateFactor | ConstantColorFactor | OneMinusConstantColorFactor | ConstantAlphaFactor | OneMinusConstantAlphaFactor
                return {
                    ...baseMetadata,
                    inset: true,
                    when: ContextKeyExpr.equals("material.blending", 5), // CustomBlending
                    description: "Defines the blending source factor of the alpha channel.",
                    enumOptions: [
                        { label: "Default", value: null },
                        { label: "ZeroFactor", value: 200 },
                        { label: "OneFactor", value: 201 },
                        { label: "SrcColorFactor", value: 202 },
                        { label: "OneMinusSrcColorFactor", value: 203 },
                        { label: "SrcAlphaFactor", value: 204 },
                        { label: "OneMinusSrcAlphaFactor", value: 205 },
                        { label: "DstAlphaFactor", value: 206 },
                        { label: "OneMinusDstAlphaFactor", value: 207 },
                        { label: "DstColorFactor", value: 208 },
                        { label: "OneMinusDstColorFactor", value: 209 },
                        { label: "SrcAlphaSaturateFactor", value: 210 },
                        { label: "ConstantColorFactor", value: 211 },
                        { label: "OneMinusConstantColorFactor", value: 212 },
                        { label: "ConstantAlphaFactor", value: 213 },
                        { label: "OneMinusConstantAlphaFactor", value: 214 },
                    ]
                };
            }
            // #endregion blending

            // #region stencil
            case "stencilFunc": {
                return {
                    ...baseMetadata,
                    when: ContextKeyExpr.equals("material.stencilWrite", true),
                    // https://github.com/mrdoob/three.js/blob/dev/src/constants.js#L1411
                    description: "Which stencil comparison function to use. Default is AlwaysStencilFunc.",
                    inset: true,
                    enumOptions: [
                        { label: "Nevel", value: 512 },
                        { label: "Less", value: 513 },
                        { label: "Equal", value: 514 },
                        { label: "LessEqual", value: 515 },
                        { label: "NotEqual", value: 516 },
                        { label: "Greater", value: 517 },
                        { label: "GreaterEqual", value: 518 },
                        { label: "Always", value: 519 },
                    ]
                };
            }
            case "stencilRef": {
                return {
                    ...baseMetadata,
                    when: ContextKeyExpr.equals("material.stencilWrite", true),
                    accept: accepts, min: 0, max: 255, step: 1,
                    defaultValue: 0,
                    inset: true,
                };
            }
            case "stencilWriteMask":
            case "stencilFuncMask": {
                return {
                    ...baseMetadata,
                    when: ContextKeyExpr.equals("material.stencilWrite", true),
                    accept: accepts, min: 0, max: 255, step: 1,
                    defaultValue: 255,
                    inset: true,
                };
            }
            case "stencilFail":
            case "stencilZFail":
            case "stencilZPass": {
                return {
                    ...baseMetadata,
                    when: ContextKeyExpr.equals("material.stencilWrite", true),
                    description: "Which stencil operation to perform when the comparison function returns true and the depth test passes.",
                    inset: true,
                    enumOptions: [
                        { label: "Keep", value: 7680 },
                        { label: "Zero", value: 0 },
                        { label: "Replace", value: 7681 },
                        { label: "Increment", value: 7682 },
                        { label: "Decrement", value: 7683 },
                        { label: "IncrementWrap", value: 34055 },
                        { label: "DecrementWrap", value: 34056 },
                        { label: "Invert", value: 5386 },
                    ]
                }
            };


            // #region wireframe

            case "wireframe": {
                return {
                    ...baseMetadata,
                    defaultValue: false,
                    description: "Render the geometry as wireframe.",
                };
            }

            case "wireframeLinejoin":
            case "wireframeLinecap": {
                return {
                    ...baseMetadata,
                    inset: true,
                    description: "Defines appearance of wireframe ends. 'round' | 'bevel' | 'miter'", enumOptions: [
                        { label: "round", value: "round" },
                        { label: "bevel", value: "bevel" },
                        { label: "miter", value: "miter" },
                    ],
                    when: ContextKeyExpr.equals("material.wireframe", true),
                };
            }

            case "wireframeLinewidth": {
                return {
                    ...baseMetadata,
                    inset: true,
                    accept: accepts, min: 0, max: 10, step: 0.1, defaultValue: 1,
                    when: ContextKeyExpr.equals("material.wireframe", true),
                };
            }

        }

        if (key_lowercase.endsWith("linewidth")) {
            return {
                ...baseMetadata,
                accept: accepts, min: 0, max: 10, step: 0.1, defaultValue: 1
            };
        }

        if (key_lowercase.endsWith("opacity") || key.endsWith("bloom")) {
            return {
                ...baseMetadata,
                accept: accepts, min: 0, max: 1, step: 0.01, defaultValue: 1
            };
        }

        if (staticPropNames.includes(key)) {
            return {

                ...baseMetadata,
                accept: accepts,
                static: true,
                description: "This property is static and cannot be changed."
            };
        }

        return {
            ...baseMetadata,
            accept: accepts
        };

        // #endregion material
    }
    // #endregion material_metadata

    // #region texture meadata

    if (isTexture(owner)) {


        switch (key) {

            case "repeat": {
                return {
                    ...baseMetadata,
                    // linked X/Y (the scale chain toggle) — tiling is usually uniform
                    constrainedProportions: true,
                    defaultValue: { x: 1, y: 1 },
                    description: "How many times the texture repeats across the surface (tiling).",
                };
            }

            case "offset": {
                return { ...baseMetadata, defaultValue: { x: 0, y: 0 }, description: "The offset of the texture in UV space." };
            }

            case "center": {
                return { ...baseMetadata, defaultValue: { x: 0.5, y: 0.5 }, description: "The center of rotation and scaling." };
            }

            case "flipY": {
                return { ...baseMetadata, defaultValue: false, description: "Whether to flip the texture vertically." };
            }

            case "premultiplyAlpha": {
                return { ...baseMetadata, description: "Whether the alpha channel is premultiplied." };
            }

            case "rotation": {
                return {
                    ...baseMetadata,
                    defaultValue: 0,
                    // symmetric slider range — the merged source for BOTH the curated
                    // texture rows and the generic ones (they used to disagree)
                    min: -Math.PI,
                    max: Math.PI,
                    step: 0.01,
                    description: "The rotation of the texture in radians."
                };
            }

            case "anisotropy": {
                return {
                    ...baseMetadata,
                    defaultValue: 1,
                    description: "The number of samples taken along the axis through the pixel that has the highest density of texels. By default, this value is 1. A higher value gives a less blurry result than a basic mipmap, at the cost of more texture samples being used.",
                    min: 1, max: 16, step: 1
                };
            }

            case "colorSpace": {
                return {
                    ...baseMetadata,
                    defaultValue: "",
                    // this case lives in the TEXTURE block — it said "material"
                    description: "The color space of the texture. Color/albedo maps are sRGB; data maps (normal, roughness, metalness, AO) must NOT be, or they are decoded as colors.",
                    enumOptions: [
                        // NoColorSpace ("") is three's DEFAULT for a plain Texture /
                        // DataTexture — without it here, such a texture has no matching
                        // option (the dropdown shows a stale/blank selection) and there
                        // is no way to set one back to it.
                        { label: "NoColorSpace", value: "" },
                        { label: "LinearSRGBColorSpace", value: "srgb-linear" },
                        { label: "SRGBColorSpace", value: "srgb" },
                    ]
                };
            }

            case "mapping": {
                return {
                    ...baseMetadata,
                    defaultValue: THREE.UVMapping,
                    enumOptions: textureMappingOptions,
                };
            }

            case "channel": {
                return {
                    ...baseMetadata,
                    defaultValue: 0,
                    description: "Lets you select the uv attribute to map the texture to. 0 for uv, 1 for uv1, 2 for uv2 and 3 for uv3",
                    enumOptions: [
                        { label: "UV Channel 0", value: 0 },
                        { label: "UV Channel 1", value: 1 },
                        { label: "UV Channel 2", value: 2 },
                        { label: "UV Channel 3", value: 3 },
                    ]
                };
            }

            case "wrapS":
            case "wrapT": {
                return {
                    ...baseMetadata,
                    defaultValue: 1001,
                    enumOptions: [
                        { label: "RepeatWrapping", value: 1000 },
                        { label: "ClampToEdgeWrapping", value: 1001 },
                        { label: "MirroredRepeatWrapping", value: 1002 },
                    ]
                }
            }
            case "magFilter":
            case "minFilter": {
                return {
                    ...baseMetadata,
                    defaultValue: 1006,
                    enumOptions: [
                        { label: "NearestFilter", value: 1003 },
                        { label: "NearestMipmapNearestFilter", value: 1004 },
                        { label: "NearestMipmapLinearFilter", value: 1005 },
                        { label: "LinearFilter", value: 1006 },
                        { label: "LinearMipmapNearestFilter", value: 1007 },
                        { label: "LinearMipmapLinearFilter", value: 1008 },
                    ]
                };
            }

            // A texture's data type, raw, is "1009". It is also the single fact that
            // separates an ordinary colour map (UnsignedByteType) from an HDR
            // environment (HalfFloatType) — which costs ~21x the VRAM.
            case "type": {
                return {
                    ...baseMetadata,
                    defaultValue: THREE.UnsignedByteType,
                    description: "How each channel is stored. UnsignedByteType is an ordinary LDR image; the float types are HDR data (an .exr/.hdr environment), which costs far more VRAM.",
                    enumOptions: attributeTypeOptions,
                };
            }

            case "format": {
                // three's Texture.format is a numeric AnyPixelFormat — which for a
                // CompressedTexture is a *compressed* value (e.g. RGB_BPTC_UNSIGNED_Format). The
                // options are derived from three's own constants so every possible value resolves
                // to a label (see texturePixelFormatOptions) instead of showing a raw number.
                return {
                    ...baseMetadata,
                    defaultValue: THREE.RGBAFormat,
                    enumOptions: texturePixelFormatOptions,
                };
            }

            case "internalFormat": {
                // three's Texture.internalFormat is a PixelFormatGPU *string* (the renderer
                // resolves it via `gl[internalFormat]`), not a GL enum number. The valid names
                // are exactly the WebGL2 sized-format constants (lib.dom). Rather than re-hardcode
                // those numbers, we list the names and type them against three's own union, so a
                // typo is a compile error and the value is directly assignable to the property.
                const internalFormats: PixelFormatGPU[] = [
                    "R8", "RG8", "RGB8", "RGBA8",
                    "SRGB8", "SRGB8_ALPHA8",
                    "R16F", "RG16F", "RGB16F", "RGBA16F",
                    "R32F", "RG32F", "RGB32F", "RGBA32F",
                ];
                return {
                    ...baseMetadata,
                    defaultValue: null,
                    enumOptions: [
                        { label: "Auto", value: null },
                        ...internalFormats.map(name => ({ label: name, value: name })),
                    ]
                };
            }
        }
    }

    // #endregion texture metadata

    if (isLight(owner)) {

        switch (key_lowercase) {
            case "intensity": {
                const val = Number(currentValue);
                return {
                    ...baseMetadata,
                    step: Math.min(0.1, val * .1),
                    defaultValue: 1,
                };
            }
            case "distance": {
                return {
                    ...baseMetadata,
                    step: 0.1,
                    defaultValue: 0
                };
            }
            case "decay": {
                return {
                    ...baseMetadata,
                    min: 0, max: 2,
                    step: 0.1,
                    defaultValue: 1
                };
            }
            case "angle": {
                return {
                    ...baseMetadata,
                    min: 0, max: Math.PI / 2, step: 0.01, defaultValue: Math.PI / 3
                };
            }
            case "penumbra": {
                return {
                    ...baseMetadata,
                    min: 0, max: 1, step: 0.01, defaultValue: 0
                };
            }
            case "power": {
                return {
                    ...baseMetadata,
                    step: 1, defaultValue: 10
                };
            }
        }
    }

    // #region Camera
    if (isCamera(owner)) {

        switch (key) {
            case "fov": {
                return {
                    ...baseMetadata,
                    min: 1, max: 180, step: 1, defaultValue: 50, description: "Field of View in degrees"
                };
            }
            case "near": {
                const current = Number(currentValue) || 1;
                const min = current * 0.01;
                const max = current * 3;
                return {
                    ...baseMetadata,
                    min, max, step: 0.01, defaultValue: current
                };
            }
            case "far": {
                const current = Number(currentValue) || 1000;
                const min = current * 0.1;
                const max = current * 3;
                return {
                    ...baseMetadata,
                    min, max, step: .1, defaultValue: current
                };
            }
            case "focus": {
                return {
                    ...baseMetadata,
                    min: 0, max: 100, step: 0.1, defaultValue: 10,
                    description: "Object distance used for stereoscopy and depth-of-field effects. This parameter does not influence the projection matrix unless a StereoCamera is being used."
                };
            }
            case "zoom": {
                return {
                    ...baseMetadata,
                    min: 0.01,
                    max: 10,
                    step: 0.1,
                    defaultValue: 1,
                    description: "Zoom factor of the camera. Default is 1."
                };
            }
            case "filmGauge": {
                return {
                    ...baseMetadata,
                    min: 1, max: 100,
                    step: 1,
                    defaultValue: 35,
                    description: "The film gauge in millimeters. This is the width of the film back that would be used in a real camera. The default is 35, which corresponds to a full-frame 35mm camera."
                };
            }
            case "filmOffset": {
                return {
                    ...baseMetadata,
                    min: -50, max: 50,
                    step: 0.1,
                    defaultValue: 0,
                    description: "The horizontal film offset in millimeters. This simulates a shift of the lens relative to the film plane."
                };
            }
            // Seen in camera on https://bruno-simon.com/
            case "coordinateSystem": {
                return {
                    ...baseMetadata,
                    static: true,
                    description: "The coordinate system used by the camera.",
                    enumOptions: [
                        { label: "WebGL CoordinateSystem", value: 2000 },
                        { label: "WebGPU CoordinateSystem", value: 2001 },
                    ]
                };
            }
        }
    }

    // #region Scene
    if (isScene(owner)) {
        const scene = owner as any;
        const backgroundTextureContextKey = `scene.${scene.id}.background.hasTexture`;
        const environmentTextureContextKey = `scene.${scene.id}.environment.hasTexture`;
        switch (key) {
            case "background":
                return {
                    ...baseMetadata,
                    accept: "null, image/*, color/*",
                    description: "The background color or texture of the scene.",
                    contextKey: backgroundTextureContextKey,
                    contextValue: value => isTextureLike(value),
                    picker: {
                        id: `background.${scene.id}`,
                        prefix: PropertyPicker.PREFIX + " hdri texture",
                    }
                };
                break;
            case "backgroundBlurriness": {
                return {
                    ...baseMetadata,
                    inset: true,
                    when: ContextKeyExpr.equals(backgroundTextureContextKey, true),
                    min: 0, max: 1, step: 0.01, defaultValue: 0, description: "Sets the background blurriness when using an environment map as background."
                };
            }
            case "backgroundIntensity": {
                return {
                    ...baseMetadata,
                    inset: true,
                    when: ContextKeyExpr.equals(backgroundTextureContextKey, true),
                    min: 0, max: 2, step: 0.1, defaultValue: 1, description: "Sets the background intensity when using an environment map as background."
                };
            }
            case "backgroundRotation": {
                return {
                    ...baseMetadata,
                    inset: true,
                    when: ContextKeyExpr.equals(backgroundTextureContextKey, true),
                    description: "The rotation of the background texture."
                };
            }
            case "environment":
                return {
                    ...baseMetadata,
                    accept: "null, image/*",
                    description: "The environment map of the scene.",
                    contextKey: environmentTextureContextKey,
                    contextValue: value => isTextureLike(value),
                    picker: {
                        id: `environment.${scene.id}`,
                        prefix: PropertyPicker.PREFIX + " hdri texture",
                    }
                };
            case "environmentIntensity": {
                return {
                    ...baseMetadata,
                    inset: true,
                    when: ContextKeyExpr.equals(environmentTextureContextKey, true),
                    min: 0, max: 2, step: 0.1, defaultValue: 1, description: "Sets the environment map intensity."
                };
            }
            case "environmentRotation": {
                return {
                    ...baseMetadata,
                    inset: true,
                    when: ContextKeyExpr.equals(environmentTextureContextKey, true),
                    description: "The rotation of the environment map."
                };
            }
            case "fog":
                return {
                    ...baseMetadata,
                    accept: "null", description: "The fog settings of the scene."
                };
                break;
        }
    }

    // #region Layers
    if (isLayermask(owner)) {

        switch (key) {
            case "mask": {
                return {
                    ...baseMetadata,
                    description: "The object's layers as a bitmask.\nA layers object assigns an 3D object to 1 or more of 32 layers numbered 0 to 31 - internally the layers are stored as a bit mask], and by default all 3D objects are a member of layer 0",
                    multiple: true,
                    enumOptions: [
                        { label: "Layer 0", value: 1 << 0 },
                        { label: "Layer 1", value: 1 << 1 },
                        { label: "Layer 2", value: 1 << 2 },
                        { label: "Layer 3", value: 1 << 3 },
                        { label: "Layer 4", value: 1 << 4 },
                        { label: "Layer 5", value: 1 << 5 },
                        { label: "Layer 6", value: 1 << 6 },
                        { label: "Layer 7", value: 1 << 7 },
                        { label: "Layer 8", value: 1 << 8 },
                        { label: "Layer 9", value: 1 << 9 },
                        { label: "Layer 10", value: 1 << 10 },
                        { label: "Layer 11", value: 1 << 11 },
                        { label: "Layer 12", value: 1 << 12 },
                        { label: "Layer 13", value: 1 << 13 },
                        { label: "Layer 14", value: 1 << 14 },
                        { label: "Layer 15", value: 1 << 15 },
                        { label: "Layer 16", value: 1 << 16 },
                        { label: "Layer 17", value: 1 << 17 },
                        { label: "Layer 18", value: 1 << 18 },
                        { label: "Layer 19", value: 1 << 19 },
                        { label: "Layer 20", value: 1 << 20 },
                        { label: "Layer 21", value: 1 << 21 },
                        { label: "Layer 22", value: 1 << 22 },
                        { label: "Layer 23", value: 1 << 23 },
                        { label: "Layer 24", value: 1 << 24 },
                        { label: "Layer 25", value: 1 << 25 },
                        { label: "Layer 26", value: 1 << 26 },
                        { label: "Layer 27", value: 1 << 27 },
                        { label: "Layer 28", value: 1 << 28 },
                        { label: "Layer 29", value: 1 << 29 },
                        { label: "Layer 30", value: 1 << 30 },
                        { label: "Layer 31", value: 1 << 31 },
                    ]
                };
            }
        }

    }

    // #region Needle component
    if (Needle.isNeedleRuntimeComponent(owner) || Needle.isNeedleGltfComponent(owner)) {

        const componentName: string | null = resolveComponentName(owner);

        if (typeof componentName === "string") {
            const desc = NeedleMeta.getPropertyDescription(componentName, key);
            if (desc) baseMetadata.description = desc;
        }

        switch (componentName) {
            case "NeedleMenu":
                if (key === "position") {
                    return {
                        ...baseMetadata,
                        enumOptions: [
                            { label: "Top", value: "top", description: "Positions the menu at the top of the canvas." },
                            { label: "Bottom", value: "bottom", description: "Positions the menu at the bottom of the canvas." },
                        ]
                    }
                }
                break;
        }

        switch (key) {
            case "guid":
            case "needle:identifier":
            case "sourceId":
                return {
                    ...baseMetadata,
                    static: true
                };
            case "enabled": {
                return {
                    description: "Whether the component is enabled or not. Enabled components can run their logic and update every frame if certain functions are implemented, while disabled components are paused and won't update.",
                    ...baseMetadata,
                    defaultValue: true
                }
            }
            // camera
            case "fieldOfView":
                {
                    const current = Number(currentValue) || 50;
                    return {
                        ...baseMetadata,
                        min: 1, max: 180, step: 1, defaultValue: current
                    };
                }
            case "ARBackgroundAlpha":
                return {
                    ...baseMetadata,
                    min: 0, max: 1, step: 0.01, defaultValue: 1
                };

            // orbitcontrols
            case "autoRotateSpeed":
                return {
                    ...baseMetadata,
                    min: -10, max: 10, step: 0.1, defaultValue: 2
                };

            case "minAzimuthAngle":
                return {
                    ...baseMetadata,
                    min: -Math.PI, max: Math.PI, step: 0.01, defaultValue: -Infinity
                };
            case "maxAzimuthAngle":
                return {
                    ...baseMetadata,
                    min: -Math.PI, max: Math.PI, step: 0.01, defaultValue: Infinity
                };
            case "minPolarAngle":
                return {
                    ...baseMetadata,
                    min: 0, max: Math.PI, step: 0.01, defaultValue: 0
                };
            case "maxPolarAngle":
                return {
                    ...baseMetadata,
                    min: 0, max: Math.PI, step: 0.01, defaultValue: Math.PI
                };

            case "dampingFactor":
                return {
                    ...baseMetadata,
                    min: 0, max: 1, step: 0.01, defaultValue: 0.1
                };
            case "minZoom":
                return {
                    ...baseMetadata,
                    min: 0, max: 100, step: 0.1, defaultValue: .1
                };
            case "maxZoom":
                return {
                    ...baseMetadata,
                    min: 0.01, max: 100, step: 0.1, defaultValue: 100
                };
            case "zoomSpeed":
                return {
                    ...baseMetadata,
                    min: 0.01, max: 10, step: 0.01, defaultValue: 1
                };

            // drag controls
            case "xrDistanceDragFactor":
                return {
                    ...baseMetadata,
                    min: 0.01, max: 10, step: 0.01, defaultValue: 1
                };

            // fog
            case "near":
                {
                    const center = Number(currentValue) || 1;
                    return {
                        ...baseMetadata,
                        min: center * .1, max: center * 2, step: .01, defaultValue: center
                    };
                }
            case "far":
                {
                    const center = Number(currentValue) || 1000;
                    return {
                        ...baseMetadata,
                        min: center * .1, max: center * 2, step: .1, defaultValue: center
                    };
                }



            // camera
            case "aspect":
                return {
                    ...baseMetadata,
                    min: 0.1, max: 4, step: 0.01, defaultValue: 1.7778
                };
            case "nearClipPlane":
                {
                    const current = Number(currentValue) || 0.1;
                    return {
                        ...baseMetadata,
                        min: current * 0.01, max: current * 3, step: 0.01, defaultValue: current
                    };
                }

            case "farClipPlane":
                {
                    const current = Number(currentValue) || 1000;
                    return {
                        ...baseMetadata,
                        min: current * 0.1, max: current * 3, step: 0.1, defaultValue: current
                    };
                }
            case "clearFlags":
                return {
                    ...baseMetadata,
                    enumOptions: [
                        { label: "None", value: 0 },
                        { label: "Skybox", value: 1 },
                        { label: "Solid Color", value: 2 },
                        { label: "Uninitialized", value: 4 },
                    ]
                };
            case "cullingMask":
                return {
                    ...baseMetadata,
                    description: "The object's layers as a bitmask.\nA layers object assigns an 3D object to 1 or more of 32 layers numbered 0 to 31 - internally the layers are stored as a bit mask], and by default all 3D objects are a member of layer 0",
                    multiple: true,
                    enumOptions: [
                        { label: "Layer 0", value: 1 << 0 },
                        { label: "Layer 1", value: 1 << 1 },
                        { label: "Layer 2", value: 1 << 2 },
                        { label: "Layer 3", value: 1 << 3 },
                        { label: "Layer 4", value: 1 << 4 },
                        { label: "Layer 5", value: 1 << 5 },
                        { label: "Layer 6", value: 1 << 6 },
                        { label: "Layer 7", value: 1 << 7 },
                        { label: "Layer 8", value: 1 << 8 },
                        { label: "Layer 9", value: 1 << 9 },
                        { label: "Layer 10", value: 1 << 10 },
                        { label: "Layer 11", value: 1 << 11 },
                        { label: "Layer 12", value: 1 << 12 },
                        { label: "Layer 13", value: 1 << 13 },
                        { label: "Layer 14", value: 1 << 14 },
                        { label: "Layer 15", value: 1 << 15 },
                        { label: "Layer 16", value: 1 << 16 },
                        { label: "Layer 17", value: 1 << 17 },
                        { label: "Layer 18", value: 1 << 18 },
                        { label: "Layer 19", value: 1 << 19 },
                        { label: "Layer 20", value: 1 << 20 },
                        { label: "Layer 21", value: 1 << 21 },
                        { label: "Layer 22", value: 1 << 22 },
                        { label: "Layer 23", value: 1 << 23 },
                        { label: "Layer 24", value: 1 << 24 },
                        { label: "Layer 25", value: 1 << 25 },
                        { label: "Layer 26", value: 1 << 26 },
                        { label: "Layer 27", value: 1 << 27 },
                        { label: "Layer 28", value: 1 << 28 },
                        { label: "Layer 29", value: 1 << 29 },
                        { label: "Layer 30", value: 1 << 30 },
                        { label: "Layer 31", value: 1 << 31 },
                    ]
                };
            case "backgroundBlurriness": {
                return {
                    ...baseMetadata,
                    min: 0, max: 1, step: 0.01, defaultValue: 0, description: "Sets the background blurriness when using an environment map as background."
                };
            }
            // case "backgroundColor": {
            //     return {
            //         ...baseMetadata,
            //         accept: "null, color/*", description: "The background color of the needle component."
            //     };
            // }


            // Needle Renderer component
            case "shadowCastingMode":
                return {
                    ...baseMetadata,
                    enumOptions: [
                        { label: "Off", value: 0, description: "No shadows are cast from this object." },
                        { label: "On", value: 1, description: "Shadows are cast from this object." },
                        { label: "TwoSided", value: 2, description: "Shadows are cast from this object, treating it as two-sided." },
                        { label: "ShadowsOnly", value: 3, description: "Object casts shadows, but is otherwise invisible in the Scene." },
                    ]
                };
                break;
            case "reflectionProbeUsage":
                return {
                    ...baseMetadata,
                    enumOptions: [
                        { label: "Off", value: 0 },
                        { label: "BlendProbes", value: 1 },
                        { label: "BlendProbesAndSkybox", value: 2 },
                        { label: "Simple", value: 3 },
                    ]
                };
            case "lightmapIndex":
                return {
                    ...baseMetadata,
                    static: true,
                    step: 1,
                    description: "The index of the lightmap applied to this object."
                };
                break;

        }


        return {
            ...baseMetadata,
        };
    }

    if (isTSLNode(owner)) {

        if (isOperatorNode(owner)) {
            switch (key) {
                case "op":
                    return {
                        ...baseMetadata,
                        enumOptions: [
                            { label: "Add", value: "+" },
                            { label: "Subtract", value: "-" },
                            { label: "Multiply", value: "*" },
                            { label: "Divide", value: "/" },
                            { label: "Modulo", value: "%" },
                            { label: "Power", value: "^" },
                            { label: "Equal", value: "==" },
                            { label: "NotEqual", value: "!=" },
                            { label: "Less", value: "<" },
                            { label: "LessEqual", value: "<=" },
                            { label: "Greater", value: ">" },
                            { label: "GreaterEqual", value: ">=" },
                            { label: "And", value: "&&" },
                            { label: "Or", value: "||" },
                        ]
                    }
            }
        }
        else if (isMathNode(owner)) {
            switch (key) {
                case "method":
                    return {
                        ...baseMetadata,
                        enumOptions: [
                            { label: "Abs", value: "abs" },
                            { label: "Ceil", value: "ceil" },
                            { label: "Floor", value: "floor" },
                            { label: "Round", value: "round" },
                            { label: "Sign", value: "sign" },
                            { label: "Sqrt", value: "sqrt" },
                            { label: "Exp", value: "exp" },
                            { label: "Log", value: "log" },
                            { label: "Sin", value: "sin" },
                            { label: "Cos", value: "cos" },
                            { label: "Tan", value: "tan" },
                            { label: "Asin", value: "asin" },
                            { label: "Acos", value: "acos" },
                            { label: "Atan", value: "atan" },
                            { label: "Sinh", value: "sinh" },
                            { label: "Cosh", value: "cosh" },
                            { label: "Tanh", value: "tanh" },
                            { label: "Asinh", value: "asinh" },
                            { label: "Acosh", value: "acosh" },
                            { label: "Atanh", value: "atanh" },
                            { label: "Exp2", value: "exp2" },
                            { label: "Log2", value: "log2" },
                            { label: "Log10", value: "log10" },
                        ]
                    }
                    break;
            }
        }
        else if(isPassNode(owner)) {
            switch(key) {
                case "scope":
                    return {
                        ...baseMetadata,
                        enumOptions: [
                            { label: "Color", value: "color" },
                            { label: "Depth", value: "depth" },
                        ]
                    }
                    break;
            }
        }

        switch (key) {
            case "_uuid":
            case "_cacheKey":
            case "_cacheKeyVersion":
            case "nodeType":
                return {
                    ...baseMetadata,
                    static: true,
                }
            case "stackTrace":
                return {
                    ...baseMetadata,
                    writeable: false,
                }
                break;

            case "updateType":
            case "updateBeforeType":
            case "updateAfterType":
                return {
                    ...baseMetadata,
                    enumOptions: [
                        { label: "None", value: "none" },
                        { label: "Frame", value: "frame" },
                        { label: "Render", value: "render" },
                        { label: "Object", value: "object" },
                    ]
                }
                break;

            case "outputColorSpace":
                // three ColorSpace constants (strings): NoColorSpace "" (the
                // RenderOutputNode default), SRGBColorSpace "srgb",
                // LinearSRGBColorSpace "srgb-linear" (NOT "linear").
                return {
                    ...baseMetadata,
                    enumOptions: [
                        { label: "Default (None)", value: "" },
                        { label: "sRGB", value: "srgb" },
                        { label: "Linear sRGB", value: "srgb-linear" },
                    ]
                }
                break;
            case "_toneMapping":
                // three ToneMapping constants are NUMERIC (No=0 … Neutral=7),
                // not strings — the node's _toneMapping is a number.
                return {
                    ...baseMetadata,
                    enumOptions: [
                        { label: "None", value: 0 },
                        { label: "Linear", value: 1 },
                        { label: "Reinhard", value: 2 },
                        { label: "Cineon", value: 3 },
                        { label: "ACES Filmic", value: 4 },
                        { label: "Custom", value: 5 },
                        { label: "AgX", value: 6 },
                        { label: "Neutral", value: 7 },
                    ]
                }
                break;
        }
    }

    if (staticPropNames.includes(key)) {
        return {
            ...baseMetadata,
            static: true,
            description: "This property is static and cannot be changed."
        };
    }

    return {
        ...baseMetadata,
    };
}


// #region Priority sorting helper

/** 
 * This value will be slightly decreased for each property
 * This is so that IF we later add properties inbetween all undefined priority properties still have *some* order.
 * For example we have materials with uniforms that might be textures (https://next.junni.co.jp/)
 * In which case unknown uniforms have undefined priority, but we still want them to be in a consistent order.
 */
let currentUndefinedPriorityOffset = -1;

function getPriorityValue(object: object, key: string, value: unknown): number {
    let priority = 1000;

    const DEFAULT_OFFSET = .001;

    if (key.startsWith("is") && value === true) {
        return priority + 10;
    }

    if (typeof object !== "object") {
        return priority;
    }

    let type = object !== null && "type" in object ? (object as any).type : null;
    if (object !== null && (object as any)["isBatchedMesh"] === true) {
        type = "BatchedMesh";
    }
    const alternativeKey = type ? `${type}.${key}` : null;

    for (const entry of priorityValues) {
        if (typeof entry === "string") {
            if (entry === key) {
                return priority;
            }
            // e.g. InstancedMesh.count
            else if (alternativeKey && entry === alternativeKey) {
                return priority;
            }
            else {
                priority -= DEFAULT_OFFSET;
            }
        } else {
            if (entry.gap !== undefined) {
                priority -= entry.gap;
            }
        }
    }

    const undefinedPriority = currentUndefinedPriorityOffset;
    currentUndefinedPriorityOffset -= DEFAULT_OFFSET;
    return undefinedPriority;
}


const priorityValues: Array<string | { gap?: number, }> = [
    "type",
    "id",
    "uuid",
    "version", // e.g. from the material
    "name",
    "visible",
    "children",
    "parent",
    "userData",
    "animations",

    // rendering related
    "castShadow",
    "receiveShadow",
    "renderOrder",
    "coordinateSystem",
    "material",
    "geometry",

    // Instancing related
    "InstancedMesh.count",
    "BatchedMesh.instanceCount",
    "BatchedMesh.maxInstanceCount",
    "BatchedMesh.unusedVertexCount",
    "BatchedMesh.unusedIndexCount",

    // skinned mesh related
    // "boundingBox",
    // "boundingSphere",
    "skeleton",
    "bindMode",

    // Basic mesh count property
    "Mesh.count",
    "SkinnedMesh.count",

    // scene related
    "background",
    "backgroundBlurriness",
    "backgroundIntensity",
    "backgroundRotation",
    "environment",
    "environmentIntensity",
    "environmentRotation",

    // transform related
    "position",
    "rotation",
    "quaternion",
    "scale",

    // camera properties
    "fov",
    "near",
    "far",
    "aspect",

    // material related
    "needsUpdate",
    "uniformsNeedUpdate",
    "precision",
    "side",
    "shadowSide",
    "flatShading",
    "colorWrite",
    "depthWrite",
    "depthTest",
    "depthFunc",
    "fog",
    "toneMapped",
    "vertexColors",
    "wireframe",
    "wireframeLinewidth",
    "wireframeLinecap",
    "wireframeLinejoin",

    "transparent",
    "alphaHash",
    "opacity",
    "alphaToCoverage",
    "dithering",
    "alphaTest",
    "premultipliedAlpha",

    "color",
    "groundColor",
    "map",
    "alphaMap",
    "aoMap",
    "aoMapIntensity",
    "bumpMap",
    "bumpScale",
    "displacementMap",
    "displacementScale",
    "displacementBias",
    "normalMap",
    "normalMapType",
    "normalScale",
    "roughnessMap",
    "roughness",
    "metalnessMap",
    "metalness",
    "envMap",
    "envMapIntensity",
    "envMapRotation",
    "lightMap",
    "lightMapIntensity",
    "emissive",
    "emissiveIntensity",
    "emissiveMap",
    "emissiveColor",
    "specularMap",
    "specularColor",
    "specularColorMap",
    "specular",
    "specularIntensity",
    "specularIntensityMap",
    "shininess",
    "reflectivity",
    "dispersion",
    "clearcoatMap",
    "clearcoat",
    "clearcoatRoughnessMap",
    "clearcoatRoughness",
    "clearcoatNormalMap",
    "clearcoatNormalScale",
    "sheen",
    "sheenColorMap",
    "sheenColor",
    "sheenRoughnessMap",
    "sheenRoughness",
    "anisotropy",
    "anisotropyMap",
    "anisotropyRotation",
    "transmissionMap",
    "transmission",
    "thicknessMap",
    "thickness",
    "opacityMap",
    "opacityMapIntensity",
    "gradientMap",
    "ior",
    "iridescence",
    "iridescenceIOR",
    "iridescenceMap",
    "iridescenceThicknessMap",
    "iridescenceThicknessRange",
    "attenuationDistance",
    "attenuationColor",


    "polygonOffset",
    "polygonOffsetFactor",
    "polygonOffsetUnits",


    // blending related
    "blending",
    "blendEquation",
    "blendSrc",
    "blendDst",
    "blendAlpha",
    "blendEquationAlpha",
    "blendSrcAlpha",
    "blendDstAlpha",
    "blendColor",

    // stencil related
    "stencilWrite",
    "stencilWriteMask",
    "stencilFunc",
    "stencilFuncMask",
    "stencilRef",
    "stencilMask",
    "stencilFail",
    "stencilZFail",
    "stencilZPass",

    "glslVersion",
    "defines",
    "vertexShader",
    "fragmentShader",
]
